import * as React from 'react';
import { useHistory } from 'react-router-dom';
import { useObservable } from 'react-use-observable';

import { Button, Dropdown, Table, Tag, Tooltip } from 'antd';
import { Icon } from '@eduzz/ui-antd-theme';

import { map } from 'rxjs/operators';
import { VERTICAL_URL } from 'settings';
import Truncate from 'v2/components/Shared/TruncateText';
import { BANK_LIST } from 'v2/helpers/constants';
import type { IBankAccount } from 'v2/interfaces/financial';
import { financialStatementService } from 'v2/services/financialStatement/statement';

import { CancelReasonModal } from '../CancelReasonModal';
import { stepsReasonsId } from '../CancelReasonModal/constants';
import type { ReasonObj } from '../CancelReasonModal/constants';
import { ConfirmationModal } from '../ConfirmationModal';

const MIN_BALANCE_TO_CREATE_ACCOUNT = 0;

type BankDetailsTableProps = {
  isFidcAuthorized: boolean;
  businessName: string;
  bankDetails: IBankAccount[];
  isLoading: boolean;
  loadBankDetails: () => void;
};

function BankDetailsTable({
  isFidcAuthorized,
  businessName,
  bankDetails,
  isLoading,
  loadBankDetails
}: BankDetailsTableProps) {
  const history = useHistory();
  const [isOpenConfirmationModal, setIsOpenConfirmationModal] = React.useState(false);
  const [isOpenCancelReasonModal, setIsOpenCancelReasonModal] = React.useState(false);
  const [actionModal, setActionModal] = React.useState<'delete' | 'mainAccount' | null>(null);
  const [selectedAccount, setSelectedAccount] = React.useState<IBankAccount | null>(null);
  const [reason, setReason] = React.useState<null | ReasonObj>(null);

  const [currentBalance] = useObservable(
    () => financialStatementService.getTotalAvailable().pipe(map(data => data.data.total)),
    []
  );

  const handleSetMainAccount = (data: IBankAccount) => {
    setActionModal('mainAccount');
    setSelectedAccount(data);
    toggleOpenConfirmationModal();
  };

  const handleDeleteAccount = (data: IBankAccount) => {
    setActionModal('delete');
    setSelectedAccount(data);
    toggleOpenConfirmationModal();
  };

  const handleShowCancelReason = (data: IBankAccount) => {
    setReason({
      bankAccountId: data.id,
      id: data.reason_disapproval_id || 0,
      description: data.reason_disapproval
    });
    toggleOpenCancelReasonModal();
  };

  const handleGoToEditStep = (data: IBankAccount) => {
    const getErrorStep = (errorId: number) => {
      const { second, third } = stepsReasonsId;
      if (second.includes(errorId)) return '2';
      if (third.includes(errorId)) return '3';
      return '1';
    };

    sessionStorage.setItem('step_error', getErrorStep(data.id));
    history.push('/producer/profile/add-bank-account', {
      bankAccountId: data.id,
      description: null,
      step: null
    });
  };

  const toggleOpenConfirmationModal = () => setIsOpenConfirmationModal(isOpen => !isOpen);
  const toggleOpenCancelReasonModal = () => setIsOpenCancelReasonModal(isOpen => !isOpen);

  const statusText = (statusSlug: string, status: string) => {
    if (statusSlug === 'active')
      return (
        <Tooltip title='A conta está aprovada e você pode torná-la padrão para saque' placement='top'>
          <Tag color='success' className='rounded-full'>
            Aprovada
          </Tag>
        </Tooltip>
      );
    if (statusSlug === 'canceled')
      return (
        <Tooltip title='Verifique o motivo de cancelamento e realize a correção do cadastro' placement='top'>
          <Tag color='error' className='rounded-full'>
            Cancelada
          </Tag>
        </Tooltip>
      );
    if (statusSlug === 'waiting_aprovation')
      return (
        <Tooltip title='Edite o cadastro para realizar o processo de verificação' placement='top'>
          <Tag className='rounded-full'>Pendente</Tag>
        </Tooltip>
      );
    if (statusSlug === 'pending' || statusSlug === 'validating_info')
      return (
        <Tooltip title='A análise do cadastro está em andamento' placement='top'>
          <Tag color='warning' className='rounded-full'>
            Em análise
          </Tag>
        </Tooltip>
      );
    if (
      statusSlug === 'waiting_pdv' ||
      statusSlug === 'waiting_document' ||
      statusSlug === 'waiting_company_certificate'
    )
      return (
        <Tooltip title='Você pode concluir este cadastro pelo aplicativo Eduzz.' placement='top'>
          <Tag icon={<Icon.Info  />} color='blue'>
            {status}
          </Tag>
        </Tooltip>
      );

    return <Tag className='rounded-full'>{status}</Tag>;
  };

  const handleToCompleteRegister = React.useCallback(() => {
    return (window.location.href = `${VERTICAL_URL}/profile/fidc`);
  }, []);

  const showRedirectToFidc =
    !isFidcAuthorized && currentBalance >= MIN_BALANCE_TO_CREATE_ACCOUNT && bankDetails?.length < 1;
  const showBalanceMessage = currentBalance <= MIN_BALANCE_TO_CREATE_ACCOUNT && bankDetails?.length < 1;
  const showEmptyMessage =
    !!isFidcAuthorized && currentBalance > MIN_BALANCE_TO_CREATE_ACCOUNT && bankDetails?.length < 1;

  const getMenuItems = (entry: IBankAccount) => {
    const items: any[] = [];

    if (entry.status_slug === 'canceled') {
      items.push({ key: 'reason', label: 'Ver motivo', onClick: () => handleShowCancelReason(entry) });
    }

    if (entry.status_slug === 'waiting_aprovation') {
      items.push({ key: 'edit', label: 'Editar', onClick: () => handleGoToEditStep(entry) });
    }

    items.push({
      key: 'main',
      label: 'Tornar conta principal',
      disabled: entry.main,
      onClick: () => handleSetMainAccount(entry)
    });
    items.push({ key: 'delete', label: 'Excluir conta', onClick: () => handleDeleteAccount(entry) });
    return items;
  };

  const columns: any[] = [
    {
      title: 'Titular',
      render: (_: any, entry: IBankAccount) => (
        <Truncate count={15} final='...'>
          {entry.name}
        </Truncate>
      )
    },
    {
      title: 'Banco',
      render: (_: any, entry: IBankAccount) => (
        <Truncate count={20} final='...'>
          {BANK_LIST[entry.bank_id as keyof typeof BANK_LIST] ?? '-'}
        </Truncate>
      )
    },
    {
      title: 'Agência',
      width: 40,
      render: (_: any, entry: IBankAccount) => {
        const agency = entry.agency_digit ? `${entry.agency}-${entry.agency_digit}` : `${entry.agency}`;
        return (
          <Truncate count={9} final='...'>
            {agency}
          </Truncate>
        );
      }
    },
    {
      title: 'Conta',
      width: 40,
      render: (_: any, entry: IBankAccount) => {
        const account = entry.account_digit ? `${entry.account}-${entry.account_digit}` : `${entry.account}`;
        return (
          <Truncate count={9} final='...'>
            {account}
          </Truncate>
        );
      }
    },
    {
      title: '',
      render: (_: any, entry: IBankAccount) => entry.main && <Icon.Star fill='currentColor' className='text-(--uizz-antd-color-link)'  />
    },
    { title: 'Moeda', render: (_: any, entry: IBankAccount) => entry.currency?.code },
    { title: 'Tipo de conta', render: (_: any, entry: IBankAccount) => entry.type },
    {
      title: 'Status',
      width: 240,
      render: (_: any, entry: IBankAccount) => statusText(entry.status_slug, entry.status)
    },
    {
      title: '',
      width: 50,
      fixed: 'right',
      render: (_: any, entry: IBankAccount) => (
        <Dropdown menu={{ items: getMenuItems(entry) }} trigger={['click']}>
          <Button type='text' shape='circle' icon={<Icon.Ellipsis rotate={90}  />} />
        </Dropdown>
      )
    }
  ];

  return (
    <>
      {showRedirectToFidc && (
        <div className='text-center'>
          <p>
            <strong>{businessName}</strong>, você deve concluir a <strong>verificação de documentos</strong> para
            cadastrar sua conta bancária da Eduzz.
          </p>
          <Button type='primary' className='mt-4' onClick={handleToCompleteRegister}>
            Iniciar validação de documentos
          </Button>
        </div>
      )}

      {showBalanceMessage && (
        <div className='text-center'>
          <p>
            <strong>{businessName}</strong>, para realizar o cadastro de uma conta bancária, é obrigatório ter realizado
            pelo menos uma venda em sua conta Eduzz.
          </p>
        </div>
      )}

      {showEmptyMessage && (
        <div className='text-center'>
          <p>
            <strong>{businessName}</strong>, você ainda não cadastrou nenhuma conta bancária na Eduzz. Toque no botão
            acima e <strong>cadastre uma conta agora</strong> mesmo.
          </p>
        </div>
      )}

      {!showRedirectToFidc && !showBalanceMessage && !showEmptyMessage && (
        <>
          <Table
            columns={columns}
            loading={isLoading}
            dataSource={bankDetails || []}
            rowKey='id'
            rowClassName='bg-surface-default'
            pagination={false}
          />

          <ConfirmationModal
            isOpen={isOpenConfirmationModal}
            setClose={toggleOpenConfirmationModal}
            action={actionModal}
            account={selectedAccount}
            loadData={loadBankDetails}
          />
          <CancelReasonModal isOpen={isOpenCancelReasonModal} setClose={toggleOpenCancelReasonModal} reason={reason} />
        </>
      )}
    </>
  );
}

export { BankDetailsTable };
